import React, { useMemo, useCallback } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PRVolumeDelaysData } from "../../../types/contractDashboard";
import styles from "./prVolumeDelays.module.scss";

interface PRVolumeDelaysProps {
  data: PRVolumeDelaysData[];
}

// Color constants
const COLORS = {
  totalPRs: "#00d2aa",
  delayedPRs: "#f59e0b",
} as const;

// Custom tooltip component for better UX
const CustomTooltip: React.FC<TooltipProps<ValueType, NameType>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const totalPRs = payload.find((p) => p.dataKey === "totalPRs");
  const delayedPRs = payload.find((p) => p.dataKey === "delayedPRs");

  const delayRate =
    totalPRs && delayedPRs
      ? ((Number(delayedPRs.value) / Number(totalPRs.value)) * 100).toFixed(1)
      : "0";

  return (
    <Box
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, marginBottom: "8px", color: "#1a1a1a" }}
      >
        {label}
      </Typography>
      {payload.map((entry, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: index === payload.length - 1 ? "4px" : "4px",
          }}
        >
          <Box
            sx={{
              width: "12px",
              height: "12px",
              borderRadius: "2px",
              backgroundColor: entry.color,
            }}
          />
          <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
            {entry.name}:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#1a1a1a", fontSize: "13px" }}
          >
            {Number(entry.value).toLocaleString()}
          </Typography>
        </Box>
      ))}
      <Box
        sx={{
          paddingTop: "6px",
          borderTop: "1px solid #f0f0f0",
          marginTop: "2px",
        }}
      >
        <Typography variant="body2" sx={{ color: "#666", fontSize: "12px" }}>
          Delay Rate: <strong>{delayRate}%</strong>
        </Typography>
      </Box>
    </Box>
  );
};

const PRVolumeDelays: React.FC<PRVolumeDelaysProps> = ({ data }) => {
  // Memoize chart data (in this case, pass through as-is)
  const chartData = useMemo(() => data, [data]);

  // Memoize Y-axis domain calculation
  const yAxisDomain = useMemo(() => {
    if (!chartData || chartData.length === 0) return [0, "auto"];
    const maxValue = Math.max(...chartData.map((d) => d.totalPRs));
    return [0, Math.ceil(maxValue * 1.1)];
  }, [chartData]);

  // Memoize Y-axis tick formatter
  const yAxisTickFormatter = useCallback((value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  }, []);

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Box className={styles.titleSection}>
            <Typography variant="h6" className={styles.title}>
              PR Volume & Delays
            </Typography>
            <Typography variant="body2" className={styles.subtitle}>
              Total PRs vs Delayed PRs over time.
            </Typography>
          </Box>
        </Box>
        <Box className={styles.chartPlaceholder}>
          <Typography variant="body2" className={styles.placeholderText}>
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h6" className={styles.title}>
            PR Volume & Delays
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Total PRs vs Delayed PRs over time.
          </Typography>
        </Box>
        <FormControl className={styles.filter}>
          <Select
            defaultValue="Last 6 Months"
            IconComponent={KeyboardArrowDownIcon}
            className={styles.select}
            aria-label="Time range filter"
          >
            <MenuItem value="Last 6 Months">Last 6 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box className={styles.chartPlaceholder}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              tickFormatter={yAxisTickFormatter}
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              tickLine={{ stroke: "#e5e7eb" }}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(0, 0, 0, 0.05)" }} />
            <Legend
              wrapperStyle={{
                paddingTop: "10px",
                fontSize: "13px",
              }}
              iconType="rect"
            />
            <Bar
              dataKey="totalPRs"
              fill={COLORS.totalPRs}
              name="Total PRs"
              animationDuration={800}
              radius={[4, 4, 0, 0]}
              barSize={40}
            />
            <Bar
              dataKey="delayedPRs"
              fill={COLORS.delayedPRs}
              name="Delayed PRs"
              animationDuration={800}
              radius={[4, 4, 0, 0]}
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(PRVolumeDelays);

